import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { GlobalService } from '../services/global.service';
import {
  RazorPayPaymentFailure,
  RazorPayPaymentResponse,
  RazorPayService,
} from '../services/razor-pay.service';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { BrandkitService } from './brandkit.service';
import { CurrencyPipe } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faCheckCircle,
  faHome,
  faRedo,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { UtilsService } from '@geekeno/ngx-website/services';

interface Order {
  id: number;
  razorpay_order_id: string;
}

@Component({
  selector: 'app-brandkit',
  imports: [FormsModule, ReactiveFormsModule, FaIconComponent, CurrencyPipe],
  templateUrl: './brandkit.component.html',
  styleUrl: './brandkit.component.scss',
})
export class BrandkitComponent {
  private globalService = inject(GlobalService);
  private destroyRef = inject(DestroyRef);
  private razorPay = inject(RazorPayService);
  private brandKitService = inject(BrandkitService);
  private utils = inject(UtilsService);
  shouldShowBookingForm = signal(false);
  shouldShowPaymentError = signal(false);
  shouldShowPaymentSuccess = signal(false);
  isSubmitting = signal(false);
  shouldShowBookingError = signal(false);
  brandKitPrice = signal(500);
  brandKitDiscount = signal(100);
  brandKitDiscountedPrice = computed(
    () => this.brandKitPrice() - this.brandKitDiscount(),
  );
  pricingCurrency = signal('USD');
  order = signal<Order | null>(null);
  icons = {
    redo: faRedo,
    cancel: faTimes,
    success: faCheckCircle,
    home: faHome,
    whatsapp: faWhatsapp,
  };

  form = new FormGroup({
    full_name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    phone: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.pattern(/^\+[1-9]\d{1,3}[-\s]?(?!0{8,14})\d{8,14}$/),
      ],
    }),
    instagram: new FormControl('', { nonNullable: true }),
    facebook: new FormControl('', { nonNullable: true }),
    linkedin: new FormControl('', { nonNullable: true }),
    goal: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  constructor() {
    this.globalService.hideFooterCTA();
    this.destroyRef.onDestroy(() => {
      this.globalService.showFooterCTA();
    });
    if (this.utils.isPlatformBrowser) {
      fetch('https://ipapi.co/json/')
        .then((res) => {
          return res.json();
        })
        .then((data) => {
          switch (data.currency) {
            case 'INR':
              this.brandKitPrice.set(14999);
              this.brandKitDiscount.set(2000);
              this.pricingCurrency.set('INR');
              break;
            case 'GBP':
              this.brandKitPrice.set(400);
              this.brandKitDiscount.set(100);
              this.pricingCurrency.set('GBP');
              break;
            case 'EUR':
              this.brandKitPrice.set(450);
              this.brandKitDiscount.set(100);
              this.pricingCurrency.set('EUR');
              break;
            default:
              this.brandKitPrice.set(500);
              this.brandKitDiscount.set(100);
              this.pricingCurrency.set('USD');
          }
        });
    }
    this.form.valueChanges.subscribe(() => {
      this.shouldShowBookingError.set(false);
    });
  }

  showBookingForm() {
    this.shouldShowBookingForm.set(true);
  }

  hideBookingForm() {
    this.shouldShowBookingForm.set(false);
  }

  hidePaymentError() {
    this.shouldShowPaymentError.set(false);
  }

  hidePaymentSuccess() {
    this.shouldShowPaymentSuccess.set(false);
  }

  createOrder() {
    this.form.markAllAsTouched();
    if (this.isSubmitting() || this.form.invalid) {
      return;
    }
    this.form.disable();
    this.isSubmitting.set(true);

    this.brandKitService
      .createOrder({
        ...this.form.value,
        amount: `${this.brandKitDiscountedPrice() * 100}`,
        currency: this.pricingCurrency(),
      })
      .subscribe({
        next: (order) => {
          this.order.set(order);
          this.hideBookingForm();
          this.attemptPayment();

          this.form.enable();
          this.isSubmitting.set(false);
        },
        error: () => {
          this.form.enable();
          this.isSubmitting.set(false);
          this.shouldShowBookingError.set(true);
        },
      });
  }

  verifyPayment(orderId: number, response: RazorPayPaymentResponse) {
    this.brandKitService
      .verifyPayment(orderId, response)
      .subscribe(() => this.shouldShowPaymentSuccess.set(true));
  }

  attemptPayment() {
    const order = this.order()!;
    this.razorPay.collectPayment(
      this.brandKitDiscountedPrice() * 100,
      this.pricingCurrency(),
      'Personal Brand Kit',
      order.razorpay_order_id,
      {
        name: this.form.controls.full_name.value,
        email: this.form.controls.email.value,
        contact: this.form.controls.phone.value,
      },
      (response) => this.verifyPayment(order.id, response),
      (response) => this.handlePaymentFailure(response, order),
    );
  }

  handlePaymentFailure(response: RazorPayPaymentFailure, order: Order) {
    this.shouldShowPaymentError.set(true);
  }
}
